import { z } from "zod";
import { paginationMetaDtoSchema } from "@nasam/kernel";

const required = z.string().trim().min(1);

export const driverStatusDtoSchema = z.enum([
  "PENDING_ACTIVATION",
  "ACTIVE",
  "INACTIVE",
]);

const namedRefDtoSchema = z.object({
  id: required,
  nameEn: z.string(),
  nameAr: z.string(),
});

export const driverDocumentDtoSchema = z.object({
  id: required,
  fileUrl: required,
  extension: required,
  createdAt: required,
});

// The one row shape every /drivers endpoint returns. Dates arrive as ISO
// date-times (`2030-01-15T00:00:00.000Z`); the mapper cuts them to dates.
export const driverDtoSchema = z.object({
  id: required,
  code: z.string().nullable().optional(),
  nameEn: z.string(),
  nameAr: z.string(),
  phoneNumber1: z.string(),
  phoneNumber2: z.string().nullable().optional(),
  email: z.string(),
  username: z.string(),
  nationalId: z.string(),
  rfidTag: z.string().nullable().optional(),
  licenseNumber: z.string(),
  licenseIssueDate: z.string().nullable().optional(),
  licenseExpiryDate: required,
  profilePictureUrl: z.string().nullable().optional(),
  status: driverStatusDtoSchema,
  accountId: z.string().nullable().optional(),
  branchId: z.string().nullable().optional(),
  account: namedRefDtoSchema.nullable().optional(),
  branch: namedRefDtoSchema.nullable().optional(),
  // Only the detail/create/update responses carry the documents.
  documents: z.array(driverDocumentDtoSchema).optional(),
});

export const driverListResponseDtoSchema = z.object({
  data: z.array(driverDtoSchema),
  meta: paginationMetaDtoSchema,
});

export const driverResponseDtoSchema = z.object({
  message: z.string().optional(),
  data: driverDtoSchema,
});

export const driverCreateDtoSchema = z.object({
  // Staff only; a client user's company is implied by their account.
  accountId: required.optional(),
  branchId: required,
  nameAr: required,
  nameEn: required,
  phoneNumber1: required,
  phoneNumber2: required.optional(),
  email: required,
  username: required,
  nationalId: required,
  rfidTag: required.optional(),
  licenseNumber: required,
  licenseIssueDate: required,
  licenseExpiryDate: required,
});

export type DriverDto = z.infer<typeof driverDtoSchema>;
export type DriverDocumentDto = z.infer<typeof driverDocumentDtoSchema>;
export type DriverCreateDto = z.infer<typeof driverCreateDtoSchema>;

// A partial: only what changed. Null clears an optional field.
export const driverUpdateDtoSchema = z.object({
  branchId: required.optional(),
  nameAr: required.optional(),
  nameEn: required.optional(),
  phoneNumber1: required.optional(),
  phoneNumber2: required.nullable().optional(),
  rfidTag: required.nullable().optional(),
  licenseIssueDate: required.optional(),
  licenseExpiryDate: required.optional(),
  nationalId: required.optional(),
  licenseNumber: required.optional(),
  status: z.enum(["ACTIVE", "INACTIVE"]).optional(),
});

export type DriverUpdateDto = z.infer<typeof driverUpdateDtoSchema>;

export const driverMessageResponseDtoSchema = z.object({
  message: z.string().optional(),
});

// The archived list's trimmed row: no phones, license or picture.
export const archivedDriverDtoSchema = z.object({
  id: required,
  code: z.string().nullable().optional(),
  nameEn: z.string(),
  nameAr: z.string(),
  username: z.string(),
  email: z.string(),
  profilePictureUrl: z.string().nullable().optional(),
  account: namedRefDtoSchema.nullable().optional(),
  branch: namedRefDtoSchema.nullable().optional(),
  archivedAt: required,
});

export const archivedDriverListResponseDtoSchema = z.object({
  data: z.array(archivedDriverDtoSchema),
  meta: paginationMetaDtoSchema,
});

export type ArchivedDriverDto = z.infer<typeof archivedDriverDtoSchema>;

// 409 on create when the username or email belongs to an archived driver --
// same shape as ARCHIVED_DEPARTMENT_FOUND. Only `code` and the match's `id`
// are required, so a leaner `data` still offers the restore.
export const archivedDriverMatchResponseDtoSchema = z.object({
  message: z.string().optional(),
  code: z.literal("ARCHIVED_DRIVER_FOUND"),
  data: z.object({
    id: required,
    nameEn: z.string().optional(),
    nameAr: z.string().optional(),
    deletedAt: z.string().nullable().optional(),
    archivedAt: z.string().nullable().optional(),
  }),
});

export type ArchivedDriverMatchDto = z.infer<
  typeof archivedDriverMatchResponseDtoSchema
>["data"];

// GET /drivers/summary: the stat cards, in the list's unfiltered scope
// (archived drivers not counted).
export const driversSummaryResponseDtoSchema = z.object({
  data: z.object({
    totalDrivers: z.number().int().nonnegative(),
    assignedDrivers: z.number().int().nonnegative(),
    unassignedDrivers: z.number().int().nonnegative(),
    expiredLicenses: z.number().int().nonnegative(),
  }),
});
